/**
 * Helpers for parsing @mentions in comment text.
 *
 * A mention is `@` followed by a name-like token. Autocomplete inserts
 * underscores, but users can also type names with spaces, e.g. @coada stefan.
 * `@` must not be preceded by a non-whitespace character (skip emails).
 */

const MENTION_REGEX = /(^|\s)@([\p{L}\p{N}._-]+)/gu
const MENTION_STOP_CHARS = /[\n\r@,;:()[\]{}<>]/

export function normalizeMentionToken(token) {
    return String(token || '').trim().replace(/\s+/g, '_').toLowerCase()
}

/**
 * Extract unique @-mention tokens from text (without the leading @).
 * When members are provided, first match the longest real member name/email
 * after each `@`, so free typing like `@coada stefan ce zici?` resolves to
 * `coada_stefan` instead of swallowing the rest of the sentence.
 */
export function extractMentionTokens(text, members = []) {
    if (!text) return []
    const seen = new Set()
    const out = []

    const add = (value) => {
        const token = normalizeMentionToken(value)
        if (!token || seen.has(token)) return
        seen.add(token)
        out.push(token)
    }

    const candidates = (members || [])
        .flatMap((member) => {
            const values = [member?.full_name, member?.email]
                .filter(Boolean)
                .map((value) => String(value).trim())
            return values.map((value) => {
                const token = normalizeMentionToken(value)
                return {
                    token,
                    variants: [
                        token,
                        token.replace(/_/g, ' '),
                    ].filter(Boolean),
                }
            })
        })
        .filter((candidate) => candidate.token)
        .sort((a, b) => b.variants[0].length - a.variants[0].length)

    if (candidates.length) {
        for (let i = 0; i < text.length; i++) {
            if (text[i] !== '@') continue
            const before = i === 0 ? ' ' : text[i - 1]
            if (!/\s/.test(before)) continue
            const rest = text.slice(i + 1).toLowerCase()
            const restLoose = rest.replace(/_/g, ' ')
            const candidate = candidates.find((item) => item.variants.some((variant) => {
                if (!restLoose.startsWith(variant)) return false
                const next = restLoose[variant.length]
                return !next || /\s|[.,!?;:()[\]{}<>]/.test(next)
            }))
            if (candidate) add(candidate.token)
        }
    }

    let match
    MENTION_REGEX.lastIndex = 0
    while ((match = MENTION_REGEX.exec(text)) !== null) {
        add(match[2])
    }
    return out
}

/**
 * File mentions are inserted as `#[filename]` so the filename (which can
 * contain spaces and dots) is unambiguously delimited. Extract the referenced
 * file names from a comment body.
 */
const FILE_MENTION_REGEX = /#\[([^\]]+)\]/g
export function extractFileMentionNames(text) {
    if (!text) return []
    const out = []
    let m
    FILE_MENTION_REGEX.lastIndex = 0
    while ((m = FILE_MENTION_REGEX.exec(text)) !== null) out.push(m[1])
    return out
}

/**
 * Active `#` file trigger at the cursor (while typing, before the bracket is
 * inserted). Returns { query, start, end } or null. `#` must start a token.
 */
export function findActiveFileMention(text, cursor) {
    if (!text || cursor === 0) return null
    const before = text.slice(0, cursor)
    const start = before.lastIndexOf('#')
    if (start < 0) return null
    const beforeHash = start === 0 ? ' ' : text[start - 1]
    if (!/\s/.test(beforeHash)) return null
    const query = before.slice(start + 1)
    if (/[\n\r#[\]]/.test(query)) return null
    if (query.split(/\s+/).filter(Boolean).length > 6) return null
    return { query, start, end: cursor }
}

/**
 * Find the active mention at the cursor position (for autocomplete).
 * Returns { query, start, end } or null.
 */
export function findActiveMention(text, cursor) {
    if (!text || cursor === 0) return null

    const beforeCursor = text.slice(0, cursor)
    const start = beforeCursor.lastIndexOf('@')
    if (start < 0) return null

    const beforeAt = start === 0 ? ' ' : text[start - 1]
    if (!/\s/.test(beforeAt)) return null

    const query = beforeCursor.slice(start + 1)
    if (MENTION_STOP_CHARS.test(query)) return null

    const words = query.trim().split(/\s+/).filter(Boolean)
    if (words.length > 4) return null

    return { query, start, end: cursor }
}
